import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import LatestStoryLayout from "@app/layouts/LatestStory/LatestStoryLayout";
import { SectionData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import {
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_L,
  GA4_REF_CODE_TITLE_STACKED,
} from "@app/utils/ga/ref-code";
import {
  mapTeaserDataToAnchor,
  mapTeaserDataToLatestProps,
} from "@app/utils/mapTeaserDataToProps";
import MetaData from "@components/Modules/MetaData";
import { mapErrorDataToMetaData } from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { configLatestStory } from "@pub/config";
import { Route } from "@route-types/LatestStory/+types/LatestStory";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { useLoaderData, useLocation } from "react-router";

import { fetchLatestStoriesData } from "./LatestStory.server";

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<SectionData, string>;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  const payload = loaderData.payload;
  const { name, path } = payload;

  return MetaData({
    title: name,
    type: "CollectionPage",
    path: path,
  });
};

export default function LatestStory() {
  const response: TRouteWithRedirect<SectionData, string> = useLoaderData();
  const location = useLocation();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }
  const data = response.payload;
  const topStory = configLatestStory.hasAnchorBlock && data.topStories[0];
  const latestArticle = configLatestStory.hasAnchorBlock
    ? data.topStories.slice(1)
    : data.topStories;

  return (
    <PubBaseLayout>
      <AdSettings
        pageType="latest"
        sectionName="latest-stories"
        adUnits="lb1,lb2,imu1,imu2,catfish,prestitial"
        pageTitle={data.name}
        currentLocation={location}
      />

      <LatestStoryLayout
        data={{
          name: configLatestStory.customTitle || data.name,
          nameZH: data.nameZH,
          anchorBlock: topStory
            ? mapTeaserDataToAnchor(topStory, GA4_REF_CODE_ANCHORBLOCK_B)
            : undefined,
          latestArticle: latestArticle.map((teaser) =>
            mapTeaserDataToLatestProps(teaser, GA4_REF_CODE_TITLE_STACKED),
          ),
          mainContentBlock: data.listedStories.map((teaser) =>
            mapTeaserDataToLatestProps(teaser, GA4_REF_CODE_CONTENTBLOCK_L),
          ),
          showVerticalLine: false,
          storyType: data.storyType,
        }}
      />
    </PubBaseLayout>
  );
}

export async function loader({
  params,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<SectionData | GlobalBaseData, GlobalBaseData | string>
> {
  const data = await fetchLatestStoriesData({
    filterTab: params.filtertab,
  });

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}
